import React from "react";
import { useState, useEffect } from "react";
import { Table, Typography, Card, Button } from "antd";

const { Title } = Typography;

const AboutComp = ({ title, area, candidates, mapImage }) => {
  const columns = [
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      align: "center",
      width: 80,
    },
    {
      title: "Candidate",
      dataIndex: "candidate",
      key: "candidate",
      align: "left",
      width: 180,
    },
    {
      title: "Party",
      dataIndex: "party",
      key: "party",
      align: "center",
      render: (text) => <strong style={{ color: "#1890ff" }}>{text}</strong>,
    },
    {
      title: "Vote Share (%)",
      dataIndex: "percentage",
      key: "percentage",
      align: "center",
      render: (text) => (
        <span style={{ fontWeight: "bold", color: "#52c41a" }}>{text}%</span>
      ),
    },
  ];

  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });

  // Zoom In
  const handleZoomIn = () => setZoom((prevZoom) => Math.min(prevZoom + 0.2, 3));

  // Zoom Out (Resets position when fully zoomed out)
  const handleZoomOut = () =>
    setZoom((prevZoom) => {
      const newZoom = Math.max(prevZoom - 0.2, 1);
      if (newZoom === 1) setPosition({ x: 0, y: 0 });
      return newZoom;
    });

  // Handle Mouse Wheel Zoom
  const handleWheel = (event) => {
    if (event.deltaY < 0) handleZoomIn();
    else handleZoomOut();
  };

  // Handle Mouse Drag Start
  const handleMouseDown = (event) => {
    if (zoom > 1) {
      setIsDragging(true);
      setStartPosition({ x: event.clientX - position.x, y: event.clientY - position.y });
    }
  };

  // Handle Mouse Drag Move
  const handleMouseMove = (event) => {
    if (!isDragging) return;
    setPosition({ x: event.clientX - startPosition.x, y: event.clientY - startPosition.y });
  };

  // Stop Dragging
  const handleMouseUp = () => setIsDragging(false);

  // Handle Keyboard Zoom
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "+") handleZoomIn();
      else if (event.key === "-") handleZoomOut();
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);


  return (
    <div style={styles.container}>
      <Card style={styles.card}>
        {/* Image Container */}
        <div
          style={styles.imageWrapper}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp} // Stop dragging when leaving
        >
          <div
            style={{
              transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
              transition: isDragging ? "none" : "transform 0.3s ease-in-out",
              cursor: zoom > 1 ? "grab" : "default",
              width: "100%",
              maxWidth: "400px", // Ensures the image is centered within a fixed width
            }}
          >
            <img src={mapImage} style={styles.image} alt="Map" />
          </div>
        </div>

        {/* Zoom Buttons */}
        <div style={styles.buttonContainer}>
          <Button onClick={handleZoomIn}>➕</Button>
          <Button onClick={handleZoomOut}>➖</Button>
        </div>

        <Title level={4} style={styles.cardTitle}>
          📍 {title} Area
        </Title>
        <p style={styles.areaText}>{area}</p>
      </Card>

      {/* Vote Share Table */}
      <Card style={styles.card}>
        <Title level={4} style={styles.cardTitle}>
          🗳️ Vote Share of Winning Candidates
        </Title>

        {/* Scrollable Table Wrapper */}
        <div style={styles.scrollableTable}>
          <Table
            columns={columns}
            dataSource={candidates}
            rowKey="_id"
            bordered
            pagination={false}
            size="middle"
          />
        </div>
      </Card>
    </div>
  );
};

// Styles
const styles = {
  container: {
    padding: "20px",
    maxWidth: "1000px",
    margin: "auto",
  },
  card: {
    marginBottom: "20px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",
    padding: "15px",
  },
  imageWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    width: "100%",
    height: "300px", // Fixed height of 300px
    cursor: "grab",
    position: "relative",
  },
  image: {
    height: "300px", // Fixed height
    width: "100%",
    objectFit: "contain",
    userSelect: "none",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "10px",
  },
  cardTitle: {
    marginBottom: "10px",
    color: "#333",
  },
  areaText: {
    background: "#f5f5f5",
    padding: "12px",
    borderRadius: "5px",
    fontSize: "15px",
    lineHeight: "1.5",
  },
  scrollableTable: {
    overflowX: "auto",
    width: "100%",
  },
};

export default AboutComp;
